import React from 'react';

import api from '@api';
import { Email, Telefone } from '@components';
import { AlunoCell } from '@components/AlunoCell';
import { formatDatePtBr } from '@utils';
import { usePageList } from '@/hooks/use-page-list';
import { useLoadingIds } from '@/hooks/use-loading-ids';
import Paginator from '@/Componentes/Paginator';
import Dropdown from '@/Componentes/Dropdown';
import ConfirmModal from '@/Componentes/Modal/ConfirmModal';
import Modal from '@/Componentes/Modal';
import RealizacaoForm from '../RealizacaoForm';
import EncerramentoForm from '../EncerramentoForm';
import StatusDetail from '@/Componentes/StatusDetail';
import pdfBlobUrl from '@/utils/pdf-blob-url';
import DownloadButton from '@/Componentes/DownloadButton';
import ProrrogacaoPrazoForm from '../ProrrogacaoPrazoForm';

export default function ListaDeEstagios() {
	const modalRealizacaoRef = React.useRef();
	const formRealizacaoRef = React.useRef();

	const modalProrrogacaoRef = React.useRef();
	const formProrrogacaoRef = React.useRef();

	const modalEncerramentoRef = React.useRef();
	const formEncerramentoRef = React.useRef();

	const [currentEstagio, setCurrentEstagio] = React.useState(null);

	const { data, setData, ...pagination } = usePageList(
		React.useCallback((p) => api.estagios.listaEstagios(p), [])
	);

	const { idIsLoading, setLoadedId, setLoadingId } = useLoadingIds();

	const baixar = async (registro, downloadType) => {
		const loadingId = downloadType === 'frequencia'
			? `estagio_frequencia_${registro}`
			: `estagio_${registro}`;

		const downloadAction = downloadType === 'frequencia'
			? api.estagios.downloadFrequencia
			: api.estagios.downloadTCE;

		const blobPropName = downloadType === 'frequencia'
			? 'frequenciaBlobUrl'
			: 'blobUrl';

		setLoadingId(loadingId);

		await downloadAction(registro).then(pdfBlobUrl)
			.then(blobUrl => {
				setData((listaDeEstagios) => {
					const estagio = listaDeEstagios.filter(estagio => estagio.tce === registro)[0];
					const estagioAtualizado = { ...estagio, [blobPropName]: blobUrl };

					const listaDeEstagiosAtualizada = listaDeEstagios.reduce((listaNova, valorOriginal) => {
						listaNova.push(valorOriginal.tce === registro ? estagioAtualizado : valorOriginal);
						return listaNova;
					}, []);

					return listaDeEstagiosAtualizada;
				});
			});

		setLoadedId(loadingId);
	};

	const validar = async (tce) => {
		setLoadingId(`estagio_${tce}`);

		await api.termosDeCompromisso.validaTermoDeCompromissoPeloRegistro(tce)
			.then(() => {
				return api.estagios.consultaEstagioPeloRegistro(tce)
					.then(estagioAtualizado => {
						setData((listaDeEstagios) => listaDeEstagios.map(estagio => estagio.tce === tce ? estagioAtualizado : estagio));
					});
			});

		setLoadedId(`estagio_${tce}`);
	};

	const regristraRealizacao = async (values) => {
		const registro = currentEstagio.tce;
		setLoadingId(`estagio_${registro}`);

		await api.estagios.registraRealizacao(registro, values)
			.then(estagioAtualizado => {
				setData((listaDeEstagios) => listaDeEstagios.map(estagio => estagio.tce === registro ? estagioAtualizado : estagio));
			});

		setLoadedId(`estagio_${registro}`);
		setCurrentEstagio(null);
		modalRealizacaoRef.current.close();
		formRealizacaoRef.current.reset();
	};

	const prorrogar = async (values) => {
		const registro = currentEstagio.tce;
		setLoadingId(`estagio_${registro}`);

		console.debug(values);
		await api.estagios.prorroga(registro, values)
			.then(estagioAtualizado => {
				setData((listaDeEstagios) => listaDeEstagios.map(estagio => estagio.tce === registro ? estagioAtualizado : estagio));
			});

		setLoadedId(`estagio_${registro}`);
		setCurrentEstagio(null);
		modalProrrogacaoRef.current.close();
		formProrrogacaoRef.current.reset();
	};

	const encerrar = async (values) => {
		const registro = currentEstagio.tce;
		setLoadingId(`estagio_${registro}`);

		console.debug(values);
		await api.estagios.encerra(registro, values)
			.then(estagioAtualizado => {
				setData((listaDeEstagios) => listaDeEstagios.map(estagio => estagio.tce === registro ? estagioAtualizado : estagio));
			});

		setLoadedId(`estagio_${registro}`);
		setCurrentEstagio(null);
		modalEncerramentoRef.current.close();
		formEncerramentoRef.current.reset();
	};

	return (
		<div>
			<div className='d-flex align-items-end'>
				<h4 className='mb-auto'>Estágios</h4>

				<div className="ms-auto" />

				{!!pagination.isLoading && <div className="me-2 text-secondary spinner-border spinner-border-sm" />}
				<Paginator pagination={pagination} align="end" />
			</div>

			<table className="table table-striped table-hover">
				<thead>
					<tr>
						<th></th>
						<th>Estagiário(a)</th>
						<th>Contrato</th>
						<th>Orientação/Supervisão</th>
						<th></th>
					</tr>
				</thead>

				<tbody>
					{data.map(({ vigencia, estagiario, campo_do_estagio, orientador, supervisor, ...estagio }, i) => (
						<tr key={estagio.tce}>
							<td>{((pagination.page - 1) * pagination.pageSize) + i + 1}</td>
							<td>
								<AlunoCell aluno={estagiario} />
							</td>
							<td>
								<div><small>N.: {estagio.tce} | Vigência: {`${formatDatePtBr(new Date(vigencia.inicio))} a ${formatDatePtBr(new Date(vigencia.fim))}`}</small></div>
								<div><small><strong>{estagio.situacao}</strong> (<StatusDetail estagio={data[i]} />)</small></div>
								<div><small>Local: {campo_do_estagio.nome}</small></div>
							</td>
							<td>
								<div><small>Orientador: {orientador.nome}</small></div>
								<div className="border-bottom" />
								<div><small>Supervisor: {supervisor.nome}</small></div>
								<div><small><Telefone value={supervisor.telefone} /> {supervisor.email && <> | <Email value={supervisor.email} /></>}</small></div>
							</td>
							<td>
								<div className="d-flex">
									<DownloadButton
										blobUrl={estagio.blobUrl}
										isLoading={idIsLoading(`estagio_${estagio.tce}`)}
										downloadFunction={() => baixar(estagio.tce)}
									/>

									<Dropdown>
										<Dropdown.Toggle className='btn btn-sm py-0'>
											<span className='fas fa-chevron-down' />
										</Dropdown.Toggle>

										<Dropdown.Menu>
											{estagio.situacao === 'TERMO_EXPEDIDO' && (
												<div className='dropdown-item'>
													<ConfirmModal
														message='Isto irá validar o termo de compromisso, tenha certeza de que todas as assinaturas já foram coletadas.'
														onConfirm={() => validar(estagio.tce)}
													>
														<button className='btn btn-sm py-0' title="Validar TCE">
															<i className="fas fa-play me-2" />Validar TCE
														</button>
													</ConfirmModal>
												</div>
											)}

											{['AGUARDANDO_VIGENCIA', 'VIGENTE', 'VIGENCIA_EXPIRADA'].includes(estagio.situacao) && (
												<div className='dropdown-item' onClick={(e) => e.stopPropagation()}>
													<DownloadButton
														blobUrl={estagio.frequenciaBlobUrl}
														isLoading={idIsLoading(`estagio_frequencia_${estagio.tce}`)}
														downloadFunction={() => baixar(estagio.tce, 'frequencia')}
													>
														<span className="ms-2">Baixar frequência</span>
													</DownloadButton>
												</div>
											)}

											{['VIGENTE', 'VIGENCIA_EXPIRADA'].includes(estagio.situacao) && (
												<div className='dropdown-item'>
													<button
														className='btn btn-sm py-0'
														title="Registrar realização"
														onClick={() => {
															setCurrentEstagio(estagio);
															modalRealizacaoRef.current.open();
														}}
													>
														<i className="fas fa-calendar-check me-2" />Registrar realização
													</button>
												</div>
											)}

											{['AGUARDANDO_RELATORIO', 'PRAZO_EXPIRADO'].includes(estagio.situacao) && (
												<div className="dropdown-item">
													<button
														className='btn btn-sm py-0'
														title="Registrar relatório"
														onClick={() => {
															setCurrentEstagio(estagio);
															modalEncerramentoRef.current.open();
														}}
													>
														<i className="fas fa-flag-checkered me-2" />Registrar relatório
													</button>
												</div>
											)}

											{['AGUARDANDO_RELATORIO', 'PRAZO_EXPIRADO'].includes(estagio.situacao) && (
												<div className="dropdown-item">
													<button
														className='btn btn-sm py-0'
														title="Registrar prorrogação de prazo"
														onClick={() => {
															setCurrentEstagio(estagio);
															modalProrrogacaoRef.current.open();
														}}
													>
														<i className="fas fa-calendar-plus me-2" />Prorrogar prazo de entrega
													</button>
												</div>
											)}

										</Dropdown.Menu>
									</Dropdown>
								</div>
								{/* <button onClick={() => aditivarTermo(estagio)}>Aditar</button> */}
							</td>
						</tr>
					))}
				</tbody>
			</table>

			{!!data.length && <div className='d-flex'>
				<div className="ms-auto" />
				<Paginator pagination={pagination} align="end" />
			</div>}

			<Modal ref={modalRealizacaoRef} bsSize='md'>
				<Modal.Header title={`Registrar realização - ${currentEstagio?.tce}`} />

				<Modal.Body>
					<RealizacaoForm ref={formRealizacaoRef} values={currentEstagio} onSubmit={(v) => regristraRealizacao(v)} />
				</Modal.Body>

				<Modal.Footer>
					<button className="btn btn-secondary" onClick={() => modalRealizacaoRef.current.close()}>Cancelar</button>
					<button className="btn btn-primary" onClick={() => formRealizacaoRef.current.submit()}>Salvar</button>
				</Modal.Footer>
			</Modal>

			<Modal ref={modalProrrogacaoRef} bsSize='md'			>
				<Modal.Header title={`Prorrogar prazo de entrega do relatório - ${currentEstagio?.tce}`} />

				<Modal.Body>
					<ProrrogacaoPrazoForm ref={formProrrogacaoRef} onSubmit={(v) => prorrogar(v)} values={currentEstagio} />
				</Modal.Body>

				<Modal.Footer>
					<button className="btn btn-secondary" onClick={() => modalProrrogacaoRef.current.close()}>Cancelar</button>
					<button className="btn btn-primary" onClick={() => formProrrogacaoRef.current.submit()}>Salvar</button>
				</Modal.Footer>
			</Modal>

			<Modal ref={modalEncerramentoRef} bsSize='md'			>
				<Modal.Header title={`Encerrar - ${currentEstagio?.tce}`} />

				<Modal.Body>
					<EncerramentoForm ref={formEncerramentoRef} onSubmit={(v) => encerrar(v)} values={currentEstagio} />
				</Modal.Body>

				<Modal.Footer>
					<button className="btn btn-secondary" onClick={() => modalEncerramentoRef.current.close()}>Cancelar</button>
					<button className="btn btn-primary" onClick={() => formEncerramentoRef.current.submit()}>Salvar</button>
				</Modal.Footer>
			</Modal>
		</div >
	);
}
